import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Plus, Pencil, Trash2, Download, Send, Package, PackageCheck, Users, Euro, AlertTriangle, CheckCircle, XCircle, Mail, Clock, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import api from '@/lib/api';
import ImageUpload from '@/components/ImageUpload';
import ValidateDialog from '@/components/ValidateDialog';

export default function ExtensionAdmin() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ext, setExt] = useState(null);
  const [synthesis, setSynthesis] = useState(null);
  const [emails, setEmails] = useState([]);
  const [tab, setTab] = useState('items');

  const loadExt = useCallback(() => api.get(`/admin/extensions/${id}`).then(r => setExt(r.data)), [id]);
  const loadSynthesis = useCallback(() => api.get(`/admin/extensions/${id}/synthesis`).then(r => setSynthesis(r.data)).catch(() => {}), [id]);
  const loadEmails = useCallback(() => api.get(`/admin/extensions/${id}/emails`).then(r => setEmails(r.data)), [id]);

  useEffect(() => { loadExt(); loadSynthesis(); loadEmails(); }, [loadExt, loadSynthesis, loadEmails]);

  if (!ext) return <div className="flex items-center justify-center py-20"><div className="animate-spin h-8 w-8 border-4 border-violet-600 border-t-transparent rounded-full" /></div>;

  const statusColors = { ouvert: 'bg-emerald-100 text-emerald-800', ferme: 'bg-rose-100 text-rose-800', livre: 'bg-sky-100 text-sky-800' };
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';

  const handleStatusChange = async (status) => {
    await api.put(`/admin/extensions/${id}`, { status });
    toast.success(`Statut changé en "${status}"`);
    loadExt();
  };

  const handleImageChange = async (url) => {
    try { await api.put(`/admin/extensions/${id}`, { image_url: url }); toast.success(url ? "Visuel mis à jour" : "Visuel retiré"); loadExt(); }
    catch { toast.error("Erreur lors de la mise à jour du visuel"); }
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/extensions/${id}/export`, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `precommandes_${ext.name}.csv`; a.click();
      toast.success("Export CSV téléchargé");
    } catch { toast.error("Erreur d'export"); }
  };

  return (
    <div data-testid="extension-admin">
      <Button variant="ghost" onClick={() => navigate('/admin/extensions')} className="mb-4 -ml-3 text-slate-500 rounded-full" data-testid="ext-admin-back">
        <ArrowLeft className="h-4 w-4 mr-1" /> Extensions
      </Button>
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div className="flex items-start gap-4">
          <div className="hidden sm:block">
            <ImageUpload value={ext.image_url || ''} onChange={handleImageChange} label="Visuel" testId="ext-admin-image" />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Badge className={`text-xs ${statusColors[ext.status] || ''}`}>{ext.status}</Badge>
              {ext.tcg_name && <Badge variant="secondary" className="text-xs">{ext.tcg_name}</Badge>}
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900">{ext.name}</h1>
            <p className="text-sm text-slate-500 mt-1">Sortie : {formatDate(ext.release_date)} | Limite : {formatDate(ext.preorder_deadline)}</p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {ext.product_type === 'tcg' && (
            <Link to={`/admin/extensions/${id}/allocations`}>
              <Button className="rounded-full bg-violet-600 hover:bg-violet-700" data-testid="open-allocations-btn"><Users className="h-4 w-4 mr-2" /> Allocations & retraits</Button>
            </Link>
          )}
          <Select value={ext.status} onValueChange={handleStatusChange}>
            <SelectTrigger className="w-[140px] rounded-lg" data-testid="status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ouvert">Ouvert</SelectItem>
              <SelectItem value="ferme">Fermé</SelectItem>
              <SelectItem value="livre">Livré</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" className="rounded-full" onClick={handleExport} data-testid="export-csv-btn"><Download className="h-4 w-4 mr-1" /> CSV</Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-100 rounded-xl p-1">
          <TabsTrigger value="items" className="rounded-lg" data-testid="tab-items">Produits ({ext.items?.length || 0})</TabsTrigger>
          <TabsTrigger value="preorders" className="rounded-lg" data-testid="tab-preorders">Précommandes ({synthesis?.total_clients || 0})</TabsTrigger>
          {ext.product_type === 'tcg' && <TabsTrigger value="allocations" className="rounded-lg" data-testid="tab-allocations">Stock</TabsTrigger>}
          <TabsTrigger value="communication" className="rounded-lg" data-testid="tab-communication">Communication</TabsTrigger>
        </TabsList>

        <TabsContent value="items" className="mt-6">
          <ItemsTab extId={id} items={ext.items || []} isTcg={ext.product_type === 'tcg'} synthesis={synthesis} onRefresh={() => { loadExt(); loadSynthesis(); }} />
        </TabsContent>
        <TabsContent value="preorders" className="mt-6">
          <PreOrdersTab synthesis={synthesis} extId={id} onRefresh={loadSynthesis} />
        </TabsContent>
        <TabsContent value="allocations" className="mt-6">
          <AllocationsTab extId={id} items={ext.items || []} synthesis={synthesis} onRefresh={() => { loadExt(); loadSynthesis(); }} />
        </TabsContent>
        <TabsContent value="communication" className="mt-6">
          <CommunicationTab extId={id} ext={ext} emails={emails} onRefresh={() => { loadEmails(); loadExt(); loadSynthesis(); }} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function StockCell({ extId, item, stat, onRefresh }) {
  const [val, setVal] = useState(item.allocated_quantity != null ? String(item.allocated_quantity) : '');
  const [saving, setSaving] = useState(false);
  useEffect(() => { setVal(item.allocated_quantity != null ? String(item.allocated_quantity) : ''); }, [item.allocated_quantity]);
  const dirty = val !== (item.allocated_quantity != null ? String(item.allocated_quantity) : '');

  const save = async () => {
    if (val === '' || parseInt(val) < 0) { toast.error("Indiquez un stock valide"); return; }
    setSaving(true);
    try { await api.put(`/admin/extensions/${extId}/items/${item.id}`, { allocated_quantity: parseInt(val) }); toast.success(`Stock de « ${item.name} » : ${val}`); onRefresh(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const available = stat?.available;
  return (
    <div className="flex items-center gap-2">
      <Input type="number" min="0" value={val} onChange={e => setVal(e.target.value)} onKeyDown={e => e.key === 'Enter' && dirty && save()} placeholder="—" className="w-20 h-8 rounded-lg text-sm" data-testid={`stock-input-${item.id}`} />
      {dirty ? (
        <Button size="sm" className="h-8 rounded-full text-xs px-3" onClick={save} disabled={saving} data-testid={`stock-save-${item.id}`}><Save className="h-3 w-3 mr-1" /> OK</Button>
      ) : available != null ? (
        <span className={`text-xs font-medium whitespace-nowrap ${available <= 0 ? 'text-rose-600' : 'text-emerald-600'}`} data-testid={`stock-available-${item.id}`}>{available} attribuable{available > 1 ? 's' : ''}</span>
      ) : <span className="text-xs text-slate-400 whitespace-nowrap">Non défini</span>}
    </div>
  );
}

function ItemsTab({ extId, items, isTcg, synthesis, onRefresh }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const emptyItem = { name: '', description: '', price: '', limit_per_client: '', image_url: '', stock: '' };
  const [form, setForm] = useState(emptyItem);

  const openCreate = () => { setEditing(null); setForm(emptyItem); setDialogOpen(true); };
  const openEdit = (item) => { setEditing(item); setForm({ name: item.name, description: item.description || '', price: String(item.price), limit_per_client: item.limit_per_client ? String(item.limit_per_client) : '', image_url: item.image_url || '', stock: item.allocated_quantity != null ? String(item.allocated_quantity) : '' }); setDialogOpen(true); };

  const handleSave = async () => {
    if (!form.name || !form.price) { toast.error("Nom et prix requis"); return; }
    const data = { name: form.name, description: form.description, price: parseFloat(form.price), limit_per_client: form.limit_per_client ? parseInt(form.limit_per_client) : null, image_url: form.image_url };
    if (isTcg && form.stock !== '') data.allocated_quantity = parseInt(form.stock);
    try {
      if (editing) { await api.put(`/admin/extensions/${extId}/items/${editing.id}`, data); toast.success("Produit mis à jour"); }
      else { await api.post(`/admin/extensions/${extId}/items`, data); toast.success("Produit ajouté"); }
      setDialogOpen(false); onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Supprimer "${item.name}" ?`)) return;
    await api.delete(`/admin/extensions/${extId}/items/${item.id}`); toast.success("Produit supprimé"); onRefresh();
  };

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <h2 className="font-bold text-lg text-slate-900">Produits de l'extension</h2>
        <Button size="sm" className="rounded-full" onClick={openCreate} data-testid="add-item-btn"><Plus className="h-4 w-4 mr-1" /> Ajouter</Button>
      </div>
      {items.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl border border-slate-200">
          <Package className="h-10 w-10 mx-auto text-slate-200" />
          <p className="mt-3 text-slate-500 text-sm">Aucun produit. Ajoutez des items à cette extension.</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Nom</TableHead><TableHead>Prix</TableHead><TableHead>Limite/client</TableHead>{isTcg && <TableHead>Stock (admin)</TableHead>}<TableHead className="w-[100px]">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {items.map(item => (
                <TableRow key={item.id} data-testid={`item-row-${item.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      {item.image_url ? <img src={item.image_url} alt="" className="h-10 w-10 rounded-lg object-cover border border-slate-200 shrink-0" data-testid={`item-thumb-${item.id}`} /> : <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center shrink-0"><Package className="h-4 w-4 text-slate-300" /></div>}
                      <div><div className="font-medium">{item.name}</div>{item.description && <div className="text-xs text-slate-500">{item.description}</div>}</div>
                    </div>
                  </TableCell>
                  <TableCell className="font-semibold text-violet-600">{item.price.toFixed(2)} €</TableCell>
                  <TableCell>{item.limit_per_client || '—'}</TableCell>
                  {isTcg && <TableCell><StockCell extId={extId} item={item} stat={synthesis?.item_stats?.find(s => s.item_id === item.id)} onRefresh={onRefresh} /></TableCell>}
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(item)} data-testid={`edit-item-${item.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-500 hover:bg-rose-50" onClick={() => handleDelete(item)} data-testid={`delete-item-${item.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="item-dialog">
          <DialogHeader><DialogTitle>{editing ? 'Modifier le produit' : 'Nouveau produit'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label className="text-xs">Nom</Label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="mt-1 rounded-lg" data-testid="item-name-input" /></div>
            <div><Label className="text-xs">Description</Label><Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="mt-1 rounded-lg" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Prix (€)</Label><Input type="number" step="0.01" value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} className="mt-1 rounded-lg" data-testid="item-price-input" /></div>
              <div><Label className="text-xs">Limite/client</Label><Input type="number" value={form.limit_per_client} onChange={e => setForm(f => ({ ...f, limit_per_client: e.target.value }))} className="mt-1 rounded-lg" placeholder="Optionnel" /></div>
            </div>
            {isTcg && <div><Label className="text-xs">Stock disponible (visible uniquement côté admin)</Label><Input type="number" min="0" value={form.stock} onChange={e => setForm(f => ({ ...f, stock: e.target.value }))} className="mt-1 rounded-lg" placeholder="Ex : 12" data-testid="item-stock-input" /><p className="text-[11px] text-slate-400 mt-1">Chaque précommande validée réduit la quantité encore attribuable.</p></div>}
            <ImageUpload value={form.image_url} onChange={v => setForm(f => ({ ...f, image_url: v }))} label="Photo du produit" testId="item-image" />
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-full">Annuler</Button><Button onClick={handleSave} className="rounded-full" data-testid="item-save-btn">Sauvegarder</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function PreOrdersTab({ synthesis, extId, onRefresh }) {
  const [adjusting, setAdjusting] = useState(null);
  const [adjustQty, setAdjustQty] = useState({});
  const [validating, setValidating] = useState(null);

  if (!synthesis) return <div className="text-center py-8 text-slate-400">Chargement...</div>;

  const handleAdjust = async (poId) => {
    const items = Object.entries(adjustQty).map(([item_id, adjusted_quantity]) => ({ item_id, adjusted_quantity: parseInt(adjusted_quantity) }));
    await api.put(`/admin/preorders/${poId}/adjust`, { items });
    toast.success("Quantités ajustées"); setAdjusting(null); onRefresh();
  };

  const handleValidate = (poId) => setValidating(synthesis.preorders.find(p => p.id === poId) || null);

  const handleRefuse = async (po) => {
    if (!window.confirm(`Ne pas retenir la demande de ${po.first_name} ${po.last_name} ? Un email « quantités limitées » lui sera envoyé.`)) return;
    const res = await api.put(`/admin/preorders/${po.id}/refuse`);
    toast.success(res.data.email_sent ? "Demande non retenue, email envoyé au client" : "Demande non retenue (email non envoyé)"); onRefresh();
  };

  const pendingCount = synthesis.preorders.filter(p => p.status === 'en_attente').length;
  const handleValidateAll = async () => {
    if (!window.confirm(`Valider les ${pendingCount} demandes en attente et envoyer les emails de confirmation ?`)) return;
    const res = await api.post(`/admin/extensions/${extId}/preorders/validate-all`);
    toast.success(`${res.data.validated} précommandes validées, ${res.data.emails_sent} emails envoyés`); onRefresh();
  };

  const statusBadge = (s) => {
    const m = { en_attente: 'bg-amber-100 text-amber-800', confirmee: 'bg-emerald-100 text-emerald-800', refusee: 'bg-rose-100 text-rose-800', annulee: 'bg-slate-100 text-slate-600', retiree: 'bg-sky-100 text-sky-800' };
    const l = { en_attente: 'En attente', confirmee: 'Confirmée', refusee: 'Non retenue', annulee: 'Annulée', retiree: 'Retirée' };
    return <Badge className={`text-xs ${m[s] || ''}`}>{l[s] || s}</Badge>;
  };

  return (
    <>
      <ValidateDialog order={validating} open={!!validating} onClose={() => setValidating(null)} onDone={onRefresh} />
      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-slate-200 rounded-xl p-4"><div className="flex items-center gap-2 text-slate-500 text-sm"><Users className="h-4 w-4" /> Clients</div><p className="text-2xl font-bold mt-1">{synthesis.total_clients}</p></div>
        <div className="bg-white border border-slate-200 rounded-xl p-4"><div className="flex items-center gap-2 text-slate-500 text-sm"><Euro className="h-4 w-4" /> CA prévisionnel</div><p className="text-2xl font-bold mt-1 text-violet-600">{synthesis.total_revenue.toFixed(2)} €</p></div>
        <div className="bg-white border border-slate-200 rounded-xl p-4"><div className="flex items-center gap-2 text-slate-500 text-sm"><Package className="h-4 w-4" /> Produits</div><p className="text-2xl font-bold mt-1">{synthesis.item_stats.length}</p></div>
      </div>
      {synthesis.item_stats.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden mb-6">
          <div className="p-4 border-b"><h3 className="font-semibold text-sm text-slate-700">Synthèse par produit</h3></div>
          <Table><TableHeader><TableRow><TableHead>Produit</TableHead><TableHead>Qté demandée</TableHead><TableHead>Stock</TableHead><TableHead>Clients</TableHead><TableHead>CA</TableHead></TableRow></TableHeader>
            <TableBody>{synthesis.item_stats.map(is => (
              <TableRow key={is.item_id}><TableCell className="font-medium">{is.item_name}</TableCell><TableCell>{is.total_demanded}</TableCell><TableCell>{is.allocated_quantity != null ? is.allocated_quantity : <span className="text-slate-400">—</span>}</TableCell><TableCell>{is.client_count}</TableCell><TableCell className="font-semibold text-violet-600">{is.revenue.toFixed(2)} €</TableCell></TableRow>
            ))}</TableBody>
          </Table>
        </div>
      )}
      {synthesis.preorders.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl border border-slate-200"><Users className="h-10 w-10 mx-auto text-slate-200" /><p className="mt-3 text-slate-500 text-sm">Aucune précommande</p></div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="p-4 border-b flex items-center justify-between gap-3 flex-wrap">
            <h3 className="font-semibold text-sm text-slate-700">Détail des précommandes</h3>
            {pendingCount > 0 && (
              <Button size="sm" className="rounded-full text-xs h-8 bg-emerald-600 hover:bg-emerald-700" onClick={handleValidateAll} data-testid="validate-all-btn">
                <CheckCircle className="h-3 w-3 mr-1" /> Valider les {pendingCount} en attente + envoyer les emails
              </Button>
            )}
          </div>
          <div className="divide-y">
            {synthesis.preorders.map(po => (
              <div key={po.id} className="p-4" data-testid={`po-row-${po.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="font-medium text-slate-900">{po.first_name} {po.last_name}</p>
                    <p className="text-xs text-slate-500">{po.email} | {po.phone} | {po.delivery_method === 'retrait' ? 'Retrait' : 'Expédition'}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {statusBadge(po.status)}
                    <span className="font-bold text-violet-600">{po.total.toFixed(2)} €</span>
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {po.items.map((oi, i) => (
                    <span key={i} className="text-xs bg-slate-50 border rounded-lg px-2 py-1">
                      {oi.item_name}: {oi.quantity}{oi.adjusted_quantity != null && oi.adjusted_quantity !== oi.quantity ? <span className="text-amber-600 font-medium"> → {oi.adjusted_quantity}</span> : ''}
                    </span>
                  ))}
                </div>
                {adjusting === po.id ? (
                  <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs font-medium text-slate-700 mb-2">Ajuster les quantités :</p>
                    <div className="flex flex-wrap gap-2">
                      {po.items.map((oi, i) => (
                        <div key={i} className="flex items-center gap-1">
                          <span className="text-xs text-slate-600">{oi.item_name}:</span>
                          <Input type="number" className="w-16 h-7 text-xs rounded" value={adjustQty[oi.item_id] ?? oi.adjusted_quantity ?? oi.quantity} onChange={e => setAdjustQty(q => ({ ...q, [oi.item_id]: e.target.value }))} />
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2 mt-2">
                      <Button size="sm" className="rounded-full text-xs h-7" onClick={() => handleAdjust(po.id)} data-testid={`save-adjust-${po.id}`}><Save className="h-3 w-3 mr-1" />Sauvegarder</Button>
                      <Button size="sm" variant="ghost" className="rounded-full text-xs h-7" onClick={() => setAdjusting(null)}>Annuler</Button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-2 flex gap-2">
                    <Button size="sm" variant="outline" className="rounded-full text-xs h-7" onClick={() => { setAdjusting(po.id); setAdjustQty({}); }} data-testid={`adjust-btn-${po.id}`}><Pencil className="h-3 w-3 mr-1" />Ajuster</Button>
                    {po.status === 'en_attente' && <Button size="sm" variant="outline" className="rounded-full text-xs h-7 text-emerald-600 hover:bg-emerald-50" onClick={() => handleValidate(po.id)} data-testid={`validate-btn-${po.id}`}><CheckCircle className="h-3 w-3 mr-1" />Valider + email</Button>}
                    {po.status === 'en_attente' && <Button size="sm" variant="outline" className="rounded-full text-xs h-7 text-rose-600 hover:bg-rose-50" onClick={() => handleRefuse(po)} data-testid={`refuse-btn-${po.id}`}><XCircle className="h-3 w-3 mr-1" />Non retenu + email</Button>}
                    {po.emails?.length > 0 && <span className="text-xs text-slate-400 self-center flex items-center gap-1"><Mail className="h-3 w-3" /> {po.emails.length} email{po.emails.length > 1 ? 's' : ''} envoyé{po.emails.length > 1 ? 's' : ''}</span>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

function AllocationsTab({ extId, items, synthesis, onRefresh }) {
  const [allocs, setAllocs] = useState({});

  useEffect(() => {
    const init = {};
    items.forEach(i => { init[i.id] = i.allocated_quantity != null ? String(i.allocated_quantity) : ''; });
    setAllocs(init);
  }, [items]);

  const handleSave = async () => {
    const allocations = Object.entries(allocs).filter(([, v]) => v !== '').map(([item_id, v]) => ({ item_id, allocated_quantity: parseInt(v) }));
    await api.put(`/admin/extensions/${extId}/allocations`, { allocations });
    toast.success("Stock sauvegardé"); onRefresh();
  };

  const getItemDemand = (itemId) => synthesis?.item_stats?.find(s => s.item_id === itemId)?.total_demanded || 0;
  const getItemAllocated = (itemId) => synthesis?.item_stats?.find(s => s.item_id === itemId)?.allocated || 0;

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <div><h2 className="font-bold text-lg text-slate-900">Stock attribuable par produit</h2><p className="text-xs text-slate-500">Visible uniquement côté admin. Chaque précommande validée réduit la quantité encore attribuable.</p></div>
        <Button size="sm" className="rounded-full" onClick={handleSave} data-testid="save-allocations-btn"><Save className="h-4 w-4 mr-1" /> Sauvegarder</Button>
      </div>
      {items.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl border border-slate-200"><Package className="h-10 w-10 mx-auto text-slate-200" /><p className="mt-3 text-slate-500 text-sm">Aucun produit dans cette extension</p></div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Produit</TableHead><TableHead>Demandé</TableHead><TableHead>Déjà attribué</TableHead><TableHead>Stock</TableHead><TableHead>Statut</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {items.map(item => {
                const demand = getItemDemand(item.id);
                const allocated = allocs[item.id] ? parseInt(allocs[item.id]) : null;
                const isShort = allocated != null && demand > allocated;
                return (
                  <TableRow key={item.id} className={isShort ? 'bg-rose-50' : ''} data-testid={`alloc-row-${item.id}`}>
                    <TableCell className="font-medium">{item.name}</TableCell>
                    <TableCell className="font-semibold">{demand}</TableCell>
                    <TableCell className="font-semibold text-sky-700">{getItemAllocated(item.id)}</TableCell>
                    <TableCell>
                      <Input type="number" className="w-24 h-8 rounded-lg text-sm" value={allocs[item.id] || ''} onChange={e => setAllocs(a => ({ ...a, [item.id]: e.target.value }))} placeholder="—" data-testid={`alloc-input-${item.id}`} />
                    </TableCell>
                    <TableCell>
                      {allocated == null ? <span className="text-slate-400 text-sm">Non défini</span> : isShort ? (
                        <div className="flex items-center gap-1 text-rose-600 text-sm font-medium"><AlertTriangle className="h-4 w-4" /> Insuffisant (-{demand - allocated})</div>
                      ) : (
                        <div className="flex items-center gap-1 text-emerald-600 text-sm font-medium"><CheckCircle className="h-4 w-4" /> Suffisant</div>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
      {synthesis?.preorders && items.some(item => {
        const demand = getItemDemand(item.id);
        const allocated = allocs[item.id] ? parseInt(allocs[item.id]) : null;
        return allocated != null && demand > allocated;
      }) && (
        <div className="mt-6 bg-amber-50 border border-amber-200 rounded-2xl p-5">
          <h3 className="font-semibold text-amber-800 flex items-center gap-2"><AlertTriangle className="h-5 w-5" /> Clients potentiellement impactés</h3>
          <p className="text-sm text-amber-700 mt-1">Les clients suivants ont commandé des produits en quantité insuffisante :</p>
          <div className="mt-3 space-y-2">
            {synthesis.preorders.map(po => {
              const impacted = po.items.some(oi => {
                const allocated = allocs[oi.item_id] ? parseInt(allocs[oi.item_id]) : null;
                const demand = getItemDemand(oi.item_id);
                return allocated != null && demand > allocated;
              });
              if (!impacted) return null;
              return (
                <div key={po.id} className="text-sm bg-white rounded-lg p-3 border border-amber-100" data-testid={`impacted-client-${po.id}`}>
                  <span className="font-medium">{po.first_name} {po.last_name}</span> — {po.email}
                  <div className="flex flex-wrap gap-1 mt-1">
                    {po.items.map((oi, i) => <Badge key={i} variant="secondary" className="text-xs">{oi.item_name}: {oi.quantity}</Badge>)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}

function ArrivalCard({ extId, ext, onRefresh }) {
  const [form, setForm] = useState({ note: '', pickup_deadline: '', mark_delivered: true });
  const [sending, setSending] = useState(false);
  const [confirmedCount, setConfirmedCount] = useState(null);

  useEffect(() => {
    api.get(`/admin/extensions/${extId}/preorders`).then(r => setConfirmedCount(r.data.filter(p => p.status === 'confirmee').length)).catch(() => setConfirmedCount(0));
  }, [extId, ext.status]);

  const send = async () => {
    if (!window.confirm(`Prévenir les ${confirmedCount} client(s) confirmé(s) que leurs produits sont arrivés ?`)) return;
    setSending(true);
    try {
      const r = await api.post(`/admin/extensions/${extId}/emails/arrival`, { ...form, pickup_deadline: form.pickup_deadline || null });
      toast.success(`Email d'arrivage envoyé à ${r.data.sent_count} client${r.data.sent_count > 1 ? 's' : ''}${r.data.failed.length ? ` (${r.data.failed.length} échec)` : ''}`);
      setForm({ note: '', pickup_deadline: '', mark_delivered: true });
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur d'envoi"); }
    finally { setSending(false); }
  };

  return (
    <div className="bg-emerald-600 text-white rounded-2xl p-6 mb-6 shadow-lg shadow-emerald-600/20" data-testid="arrival-card">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex-1 min-w-[260px]">
          <div className="flex items-center gap-2"><PackageCheck className="h-5 w-5" /><h2 className="font-bold text-lg">Les produits sont arrivés</h2></div>
          <p className="text-emerald-100 text-sm mt-1">En un clic, chaque client confirmé reçoit le détail de sa commande et l'invitation à venir la récupérer (ou l'info d'expédition).</p>
          <div className="grid sm:grid-cols-2 gap-3 mt-4">
            <div>
              <Label className="text-xs text-emerald-100">À récupérer avant le (optionnel)</Label>
              <Input type="date" value={form.pickup_deadline} onChange={e => setForm(f => ({ ...f, pickup_deadline: e.target.value }))} className="mt-1 rounded-lg bg-white/95 text-slate-900 border-0" data-testid="arrival-deadline-input" />
            </div>
            <div>
              <Label className="text-xs text-emerald-100">Mot de la boutique (optionnel)</Label>
              <Input value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} placeholder="Ex : ouvert jusqu'à 19h ce samedi !" className="mt-1 rounded-lg bg-white/95 text-slate-900 border-0" data-testid="arrival-note-input" />
            </div>
          </div>
          <label className="flex items-center gap-2 mt-3 text-sm text-emerald-50 cursor-pointer">
            <input type="checkbox" checked={form.mark_delivered} onChange={e => setForm(f => ({ ...f, mark_delivered: e.target.checked }))} className="h-4 w-4 rounded accent-white" data-testid="arrival-mark-delivered" />
            Passer l'extension en « Livré » après l'envoi
          </label>
        </div>
        <div className="text-right">
          <p className="text-3xl font-bold" data-testid="arrival-confirmed-count">{confirmedCount ?? '…'}</p>
          <p className="text-xs text-emerald-100 mb-3">client{confirmedCount > 1 ? 's' : ''} confirmé{confirmedCount > 1 ? 's' : ''}</p>
          <Button onClick={send} disabled={sending || !confirmedCount} className="rounded-full bg-white text-emerald-700 hover:bg-emerald-50 font-semibold" data-testid="arrival-send-btn">
            <Send className="h-4 w-4 mr-2" /> {sending ? 'Envoi...' : 'Annoncer l\'arrivage'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function CommunicationTab({ extId, ext, emails, onRefresh }) {
  const [form, setForm] = useState({ subject: '', message: '', email_type: 'information' });
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!form.subject || !form.message) { toast.error("Sujet et message requis"); return; }
    setSending(true);
    try {
      const r = await api.post(`/admin/extensions/${extId}/emails`, form);
      toast.success(`Email envoyé à ${r.data.sent_count} destinataire${r.data.sent_count > 1 ? 's' : ''}`);
      setForm({ subject: '', message: '', email_type: 'information' });
      onRefresh();
    } catch { toast.error("Erreur d'envoi"); }
    finally { setSending(false); }
  };

  return (
    <>
      <ArrivalCard extId={extId} ext={ext} onRefresh={onRefresh} />
      <div className="bg-white border border-slate-200 rounded-2xl p-6 mb-6">
        <h2 className="font-bold text-lg text-slate-900 mb-4 flex items-center gap-2"><Send className="h-5 w-5 text-violet-600" /> Envoyer un email groupé</h2>
        <div className="space-y-4">
          <div>
            <Label className="text-xs">Type</Label>
            <Select value={form.email_type} onValueChange={v => setForm(f => ({ ...f, email_type: v }))}>
              <SelectTrigger className="mt-1 rounded-lg" data-testid="email-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="confirmation_allocation">Confirmation allocation</SelectItem>
                <SelectItem value="demande_paiement">Demande de paiement</SelectItem>
                <SelectItem value="information">Information générale</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Sujet</Label><Input value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} className="mt-1 rounded-lg" data-testid="email-subject-input" /></div>
          <div><Label className="text-xs">Message</Label><Textarea value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} className="mt-1 rounded-lg min-h-[120px]" data-testid="email-message-input" /></div>
          <Button className="rounded-full" onClick={handleSend} disabled={sending} data-testid="send-email-btn">
            <Mail className="h-4 w-4 mr-2" /> {sending ? 'Envoi...' : 'Envoyer'}
          </Button>
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <h2 className="font-bold text-lg text-slate-900 mb-4">Historique des emails</h2>
        {emails.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-8">Aucun email envoyé</p>
        ) : (
          <div className="space-y-3">
            {emails.map(em => (
              <div key={em.id} className="p-4 bg-slate-50 rounded-xl" data-testid={`email-log-${em.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <Badge variant="secondary" className="text-xs mb-1">{em.email_type}</Badge>
                    <p className="font-medium text-sm text-slate-900">{em.subject}</p>
                    <p className="text-xs text-slate-500 mt-1 line-clamp-2">{em.message}</p>
                  </div>
                  <div className="text-right shrink-0 ml-4">
                    <p className="text-xs text-slate-500 flex items-center gap-1"><Clock className="h-3 w-3" />{new Date(em.sent_at).toLocaleDateString('fr-FR')}</p>
                    <p className="text-xs text-slate-400">{em.recipients_count} destinataire{em.recipients_count > 1 ? 's' : ''}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
